import axios from "axios";

// Don't add fallbacks — if REACT_APP_BACKEND_URL is missing we want to fail
// fast at build time rather than ship a broken bundle pointing at the wrong host.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// ---------- Dual-mode auth (Bearer + cookie) ----------
// Feb 2026 production incident: third-party cookies set by the backend are
// dropped on iPad Safari, Android Chrome, Brave, and most mobile/PWA browsers
// (ITP + cross-site cookie phase-out). The cookie still works on permissive
// desktop browsers, so we keep BOTH paths: every login response carries the
// raw JWT in its body, we persist it in localStorage, and attach it as
// `Authorization: Bearer` on every request. The backend's `get_current_user`
// accepts either source.
const TOKEN_KEY = "syllox_auth_token";

export function getToken() {
  try { return localStorage.getItem(TOKEN_KEY) || null; } catch { return null; }
}

export function setToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    // localStorage may throw in private-mode Safari quotas; degrade silently
    // (the cookie path will still work where the browser allows it).
  }
}

export function clearToken() {
  setToken(null);
}

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Inject Bearer token on every request. Cheap (sync) — no perf cost.
api.interceptors.request.use((cfg) => {
  const t = getToken();
  if (t) {
    cfg.headers = cfg.headers || {};
    if (!cfg.headers.Authorization) cfg.headers.Authorization = `Bearer ${t}`;
  }
  return cfg;
});

// Diagnostic interceptor (May 2026): all failing requests get logged with
// enough context to debug cross-device save failures. We log:
//   - method + URL
//   - HTTP status + backend detail
//   - axios error code (e.g. ERR_NETWORK / ECONNABORTED)
//   - whether we even got a response (network drop vs server error)
// Anything mounted via `api.post/patch/delete` benefits automatically — no
// per-call wiring required.
api.interceptors.response.use(
  (resp) => resp,
  (err) => {
    const cfg = err?.config || {};
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail;
    // eslint-disable-next-line no-console
    console.error("[api]", cfg.method?.toUpperCase(), cfg.url, {
      status,
      code: err?.code,
      detail,
      hadResponse: !!err?.response,
      message: err?.message,
    });
    return Promise.reject(err);
  }
);

export default api;

export function formatApiError(detail) {
  if (detail == null) return "Couldn't reach the server. Please check your connection and try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function formatSeconds(secs) {
  const s = Math.max(0, Math.floor(secs || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${r}s`;
  return `${r}s`;
}

export function pad(n) {
  return String(n).padStart(2, "0");
}

export function hms(secs) {
  const s = Math.max(0, Math.floor(secs || 0));
  return `${pad(Math.floor(s / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
}
